import type { Step, StepStatus } from '@lucid/lego';
import { Note, Prospect } from 'src/api/services/lupp/prospect/dto.prospect';

/**
 * Prospect status steps in order
 */
export const PROSPECT_STATUS_STEPS = [
  'Open',
  'Contacting',
  'Engaged',
  'Quote Generated',
  'Quote Accepted',
  'Ordered',
  'Delivered',
  'Closed',
];

/**
 * Get stepper steps based on current status
 * @param currentStatus - The current status name
 * @returns Array of Step objects for the Stepper component
 */
export const getStepperSteps = (currentStatus: string | undefined | null): Step[] => {
  // If statusName is null or undefined, default to "Open" (index 0)
  const currentIndex = currentStatus
    ? PROSPECT_STATUS_STEPS.findIndex((step) => step.toLowerCase() === currentStatus.toLowerCase())
    : 0;

  const normalizedStatus = currentStatus?.toLowerCase() || 'open';

  return PROSPECT_STATUS_STEPS.map((label, index) => {
    let isDisabled = true; // Default to disabled
    let isHighlighted = false;

    // Determine if step is clickable based on status transition rules
    if (label === 'Closed') {
      // Closed is clickable from any status except when already Closed or Delivered
      isDisabled = normalizedStatus === 'closed' || normalizedStatus === 'delivered';
      isHighlighted = !isDisabled;
    } else if (label === 'Engaged') {
      // Engaged is clickable only from Open or Contacting
      isDisabled = !(normalizedStatus === 'open' || normalizedStatus === 'contacting');
    } else if (label === 'Contacting') {
      // Contacting is clickable only from Open
      isDisabled = normalizedStatus !== 'open';
    }
    // All other steps (Quote Generated, Quote Accepted, Ordered, Delivered) are not clickable

    // Determine step status
    let stepStatus: StepStatus;
    if (index < currentIndex) {
      stepStatus = 'completed';
    } else if (index === currentIndex) {
      // If current status is Closed, show it as completed (with checkmark)
      stepStatus = normalizedStatus === 'closed' && label === 'Closed' ? 'completed' : 'active';
    } else {
      stepStatus = 'pending';
    }

    return {
      id: `step-${index}`,
      label,
      status: stepStatus,
      disabled: isDisabled,
      highlighted: isHighlighted,
    };
  }) as Step[];
};

export type Theme = 'light' | 'dark';

/**
 * Format a Date object as OffsetDateTime string (ISO 8601 format without milliseconds)
 * @param date - The date to format
 * @returns Formatted date string in the format: YYYY-MM-DDTHH:mm:ss+00:00
 * @example
 * ```ts
 * formatOffsetDateTime(new Date()) // "2025-12-19T15:23:19+00:00"
 * ```
 */
export const formatOffsetDateTime = (date: Date): string => {
  return date.toISOString().replace(/\.\d{3}Z$/, '+00:00');
};

/**
 * Format date from datetime string for date picker
 * @param datetime - ISO datetime string or any valid date string
 * @returns Date string in format YYYY-MM-DD or empty string if invalid
 * @example
 * ```ts
 * formatDateForPicker('2024-12-19T15:23:19+00:00') // "2024-12-19"
 * formatDateForPicker(null) // ""
 * ```
 */
export const formatDateForPicker = (datetime?: string | null): string => {
  if (!datetime) return '';
  try {
    const date = new Date(datetime);
    return date.toISOString().split('T')[0];
  } catch {
    return '';
  }
};

/**
 * Get the next engagement date from contact tracking when status is Engaged or Contacting
 * @param prospect - The prospect data
 * @returns Next engagement date in YYYY-MM-DD format or undefined
 * @example
 * ```ts
 * getNextEngagementDate(prospect) // "2024-12-25" or undefined
 * ```
 */
export const getNextEngagementDate = (prospect?: Prospect): string | undefined => {
  const statusName = prospect?.statusName;

  // For Engaged and Contacting status, try to get from contact tracking first
  if (statusName === 'Engaged' || statusName === 'Contacting') {
    const activeTracking = prospect?.prospectContactTrackings?.find(
      (tracking) => tracking.isActive === true
    );
    const nextToBeContacted = activeTracking?.nextToBeContacted;
    if (nextToBeContacted) {
      return formatDateForPicker(nextToBeContacted);
    }
  }

  // For all other statuses or if contact tracking is not available, return undefined
  return undefined;
};

/**
 * Get the latest note content from the notes array sorted by createdAt descending
 * @param notes - Array of prospect notes
 * @returns Latest note content or empty string
 * @example
 * ```ts
 * getLatestNoteContent(prospect.notes) // "Latest note text"
 * ```
 */
export const getLatestNoteContent = (notes?: Note[]): string => {
  if (!notes?.length) return '';

  // Sort by createdAt descending to get the latest one
  const sortedNotes = [...notes].sort((a, b) => {
    const dateA = a.createdAt ? new Date(a.createdAt).getTime() : 0;
    const dateB = b.createdAt ? new Date(b.createdAt).getTime() : 0;
    return dateB - dateA;
  });

  return sortedNotes[0]?.noteContent || '';
};

/**
 * Update prospect contact trackings with next engagement date
 * @param prospect - The prospect data
 * @param nextEngagementDate - Next engagement date string (YYYY-MM-DD or MM/DD/YYYY)
 * @param now - Current timestamp in ISO format
 * @returns Updated prospect contact trackings array with only the active tracking
 * @example
 * ```ts
 * updateProspectContactTrackings(prospect, '2026-02-05', now)
 * ```
 */
export const updateProspectContactTrackings = (
  prospect: Prospect,
  nextEngagementDate: string | undefined,
  now: string
) => {
  if (!nextEngagementDate) {
    return prospect.prospectContactTrackings || [];
  }

  const trackings = prospect.prospectContactTrackings || [];

  // Convert date to proper ISO format: YYYY-MM-DDTHH:mm:ssZ (without milliseconds)
  const dateStr = nextEngagementDate;
  const currentTime = new Date();
  let nextToBeContactedDate: string;

  // Check if date is in MM/DD/YYYY format or YYYY-MM-DD format
  if (dateStr.includes('/')) {
    // Parse MM/DD/YYYY format
    const [month, day, year] = dateStr.split('/');
    const dateObj = new Date(
      Date.UTC(
        parseInt(year),
        parseInt(month) - 1,
        parseInt(day),
        currentTime.getUTCHours(),
        currentTime.getUTCMinutes(),
        currentTime.getUTCSeconds()
      )
    );
    nextToBeContactedDate = dateObj.toISOString().replace(/\.\d{3}Z$/, 'Z');
  } else {
    // Already in YYYY-MM-DD format
    const dateObj = new Date(
      Date.UTC(
        parseInt(dateStr.split('-')[0]),
        parseInt(dateStr.split('-')[1]) - 1,
        parseInt(dateStr.split('-')[2]),
        currentTime.getUTCHours(),
        currentTime.getUTCMinutes(),
        currentTime.getUTCSeconds()
      )
    );
    nextToBeContactedDate = dateObj.toISOString().replace(/\.\d{3}Z$/, 'Z');
  }

  const activeTracking = trackings.find((t) => t.isActive === true);

  if (activeTracking) {
    // Return only the updated active tracking object
    return [
      {
        ...activeTracking,
        nextToBeContacted: nextToBeContactedDate,
        updatedDatetime: now,
        updatedBy: 'portal-user',
      },
    ];
  } else {
    // No active tracking exists, create a new one
    return [
      {
        contactedMethod: 'Engagement',
        nextToBeContacted: nextToBeContactedDate,
        isActive: true,
        isDeleted: false,
        createdDatetime: now,
        updatedDatetime: now,
        createdBy: 'portal-user',
        updatedBy: 'portal-user',
      },
    ];
  }
};

/**
 * Build prospect update payload with base fields and optional overrides
 * @param prospect - The prospect data
 * @param now - Current timestamp in ISO format
 * @param overrides - Optional fields to override the base payload
 * @returns Complete prospect update payload
 * @example
 * ```ts
 * buildProspectPayload(prospect, now, { city: 'New York' })
 * ```
 */
export const buildProspectPayload = (
  prospect: Prospect,
  now: string,
  overrides: Partial<Prospect> = {}
): Prospect => {
  return {
    // Consent fields
    isEmailOptIn: prospect.isEmailOptIn ?? true,
    isCallOptIn: prospect.isCallOptIn ?? false,
    isPrivacyConsent: prospect.isPrivacyConsent ?? true,

    // Address fields
    prospectAddressDetail: prospect.prospectAddressDetail,
    city: prospect.city,
    region: prospect.region,
    zipCode: prospect.zipCode,
    countryCode: prospect.countryCode,

    // Finance and vehicle fields
    financeType: prospect.financeType,
    currentCarMake: prospect.currentCarMake,

    // Sync and metadata
    lastSyncedToSalesforce: prospect.lastSyncedToSalesforce,
    timezone: prospect.timezone || 'UTC',
    isDeleted: false,
    noteText: prospect.noteText,

    // Nested arrays
    prospectScores: prospect.prospectScores || [],
    prospectStatusLogs: prospect.prospectStatusLogs || [],
    testDriveSchedules: prospect.testDriveSchedules || [],
    prospectContactTrackings: prospect.prospectContactTrackings || [],

    // Assignment fields
    assignedToBusinessPartner: prospect.assignedToBusinessPartner,
    prospectType: prospect.prospectType || 'Lead',
    assignedBusinessPartnerType: prospect.assignedBusinessPartnerType,
    assignedBy: prospect.assignedBy,
    assignedDatetime: prospect.assignedDatetime,
    assignmentComments: prospect.assignmentComments,
    comments: prospect.comments,

    // Prospect identifiers
    prospectIdentifier: prospect.prospectIdentifier,
    sfdcProspectIdentifier: prospect.sfdcProspectIdentifier,
    sfdcLeadIdentifier: prospect.sfdcLeadIdentifier,
    sfdcCustomerRecordNumber: prospect.sfdcCustomerRecordNumber,

    // Prospect personal info
    prospectFirstName: prospect.prospectFirstName || '',
    prospectLastName: prospect.prospectLastName || '',
    prospectEmail: prospect.prospectEmail || '',
    prospectPhone: prospect.prospectPhone,
    prospectCompanyName: prospect.prospectCompanyName,

    // Source fields
    prospectSourceName: prospect.prospectSourceName || prospect.sourceName || '',
    prospectCaptureForm: prospect.prospectCaptureForm || prospect.captureForm || '',
    prospectRecordOwner: prospect.prospectRecordOwner,
    prospectOwnerLocation: prospect.prospectOwnerLocation,
    prospectCapturedByUser: prospect.prospectCapturedByUser,
    prospectCycleMarket: prospect.prospectCycleMarket,
    recordType: prospect.recordType || 'Lead',

    // Interest fields
    interestedModel: prospect.interestedModel,
    interestedTrim: prospect.interestedTrim,
    interestedVehicleCondition: prospect.interestedVehicleCondition,
    purchaseType: prospect.purchaseType,
    purchaseTiming: prospect.purchaseTiming,
    preferredContactMethod: prospect.preferredContactMethod,

    // Audit fields
    createdDatetime: prospect.createdDatetime || prospect.createdDate,
    updatedDatetime: now,
    createdBy: prospect.createdBy,
    updatedBy: 'portal-user',

    // Status
    statusName: prospect.statusName,

    // Apply overrides
    ...overrides,
  };
};
